import { NotePencilIcon, PlusIcon, PushPinSimpleIcon, SparkleIcon } from '@phosphor-icons/react';
import { type ReactNode } from 'react';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuPortal,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { GLYPH_ICON } from '@/components/ui/object-glyph';

const CampaignIcon = GLYPH_ICON.campaigns;

export type PostCreateMenuSource = 'calendar' | 'library';

interface PostCreateMenuProps {
  source: PostCreateMenuSource;
  trigger: ReactNode;
  onCreateWithMora: () => void;
  onCreatePost: () => void;
  /**
   * The third door, and the only optional one: put something on the calendar
   * that Mora will never publish — a blog going live, a filming day, a launch.
   *
   * Optional because it is genuinely surface-specific. A calendar cell knows
   * WHICH DAY the moment starts on, which is the whole reason the row belongs
   * there; the Library's copy of this menu has no day to offer and correctly
   * shows two doors, not a third that would have to invent a date. Omit it and
   * this renders exactly as it did before the row existed.
   */
  onAddMoment?: () => void;
  /**
   * The fourth door, set apart below a separator: it leaves the publishing
   * pipeline for the paid-ad builder. Optional for the same reason as
   * `onAddMoment` — omit it and nothing renders (the Library's copy stays as-is).
   */
  onNewPaidCampaign?: () => void;
  isCreatingPost?: boolean;
  align?: 'start' | 'center' | 'end';
  /** Portal target — pass the enclosing DialogContent's node (via
   *  `useDialogContentContainer()`) when this menu opens inside a Dialog, so
   *  it can never cascade-close its own parent. See dialog.tsx. */
  container?: HTMLElement | null;
}

interface PostCreateSubmenuProps extends Omit<PostCreateMenuProps, 'trigger' | 'align'> {
  triggerClassName?: string;
}

const COPY: Record<
  PostCreateMenuSource,
  {
    aiLabel: string;
    aiDescription: string;
    draftDescription: string;
    widthClassName: string;
  }
> = {
  calendar: {
    aiLabel: 'Create posts with Mora',
    aiDescription: 'Fill open calendar slots with the AI composer',
    draftDescription: 'Upload media, write a caption, plan manually',
    widthClassName: 'w-72',
  },
  library: {
    aiLabel: 'Create with Mora',
    aiDescription: 'Send this image to Mora AI as a reference.',
    draftDescription: 'Write, schedule, and publish manually',
    widthClassName: 'w-64',
  },
};

/**
 * Shared two-door creation menu. The trigger and the contextual explanation
 * intentionally stay with their source surface; the choice structure and
 * close-before-transition behavior do not drift.
 */
export function PostCreateMenu({
  source,
  trigger,
  onCreateWithMora,
  onCreatePost,
  onAddMoment,
  onNewPaidCampaign,
  isCreatingPost = false,
  align = 'end',
  container,
}: PostCreateMenuProps): ReactNode {
  const copy = COPY[source];
  const runAfterClose = (callback: () => void): void => {
    window.setTimeout(callback, 0);
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>{trigger}</DropdownMenuTrigger>
      <DropdownMenuContent
        align={align}
        className={`${copy.widthClassName} p-1`}
        container={container}
      >
        <DropdownMenuGroup>
          <DropdownMenuItem
            className="items-start gap-2.5 rounded-lg px-2.5 py-2.5"
            disabled={isCreatingPost}
            onSelect={() => runAfterClose(onCreateWithMora)}
          >
            <span className="mt-0.5 flex size-5 shrink-0 items-center justify-center text-muted-foreground">
              <SparkleIcon className="size-4" weight="regular" />
            </span>
            <span className="min-w-0">
              <span className="block text-caption font-medium text-foreground">{copy.aiLabel}</span>
              <span className="mt-0.5 block text-detail leading-snug text-muted-foreground">
                {copy.aiDescription}
              </span>
            </span>
          </DropdownMenuItem>
          <DropdownMenuItem
            className="items-start gap-2.5 rounded-lg px-2.5 py-2.5"
            disabled={isCreatingPost}
            onSelect={() => runAfterClose(onCreatePost)}
          >
            <span className="mt-0.5 flex size-5 shrink-0 items-center justify-center text-muted-foreground">
              <NotePencilIcon className="size-4" weight="regular" />
            </span>
            <span className="min-w-0">
              <span className="block text-caption font-medium text-foreground">New draft</span>
              <span className="mt-0.5 block text-detail leading-snug text-muted-foreground">
                {copy.draftDescription}
              </span>
            </span>
          </DropdownMenuItem>
          {onAddMoment && (
            /* Third, and last. It sits below the two Mora doors because it is
               the one that does NOT enter the publishing pipeline — the order
               reads "have Mora write it / write it yourself / just note that
               it is happening", which is decreasing involvement from the
               product, and that is the axis a person is actually choosing on.
               No pin colour: the glyph is a shape cue, and the class hue lives
               on the bar this creates, not on the menu row that creates it. */
            <DropdownMenuItem
              className="items-start gap-2.5 rounded-lg px-2.5 py-2.5"
              onSelect={() => runAfterClose(onAddMoment)}
            >
              <span className="mt-0.5 flex size-5 shrink-0 items-center justify-center text-muted-foreground">
                <PushPinSimpleIcon className="size-4" weight="regular" />
              </span>
              <span className="min-w-0">
                <span className="block text-caption font-medium text-foreground">
                  Add to the calendar
                </span>
                <span className="mt-0.5 block text-detail leading-snug text-muted-foreground">
                  For things Mora doesn&apos;t publish, like a blog going live, a filming day or a
                  launch
                </span>
              </span>
            </DropdownMenuItem>
          )}
        </DropdownMenuGroup>
        {onNewPaidCampaign && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuGroup>
              <DropdownMenuItem
                className="items-start gap-2.5 rounded-lg px-2.5 py-2.5"
                onSelect={() => runAfterClose(onNewPaidCampaign)}
              >
                <span className="mt-0.5 flex size-5 shrink-0 items-center justify-center text-muted-foreground">
                  <CampaignIcon className="size-4" weight="regular" />
                </span>
                <span className="min-w-0">
                  <span className="block text-caption font-medium text-foreground">
                    Paid campaign
                  </span>
                  <span className="mt-0.5 block text-detail leading-snug text-muted-foreground">
                    Draft ads for LinkedIn, Meta or Google
                  </span>
                </span>
              </DropdownMenuItem>
            </DropdownMenuGroup>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/** Overflow-compatible version of the same two-door creation choice. */
export function PostCreateSubmenu({
  source,
  onCreateWithMora,
  onCreatePost,
  isCreatingPost = false,
  triggerClassName,
}: PostCreateSubmenuProps): ReactNode {
  const copy = COPY[source];
  const runAfterClose = (callback: () => void): void => {
    window.setTimeout(callback, 0);
  };

  return (
    <DropdownMenuSub>
      <DropdownMenuSubTrigger className={triggerClassName}>
        <PlusIcon className="size-4" weight="regular" />
        <span>Create post</span>
      </DropdownMenuSubTrigger>
      <DropdownMenuPortal>
        <DropdownMenuSubContent className={copy.widthClassName}>
          <DropdownMenuItem
            className="items-start gap-2.5 rounded-lg px-2.5 py-2.5"
            disabled={isCreatingPost}
            onSelect={() => runAfterClose(onCreateWithMora)}
          >
            <span className="mt-0.5 flex size-5 shrink-0 items-center justify-center text-muted-foreground">
              <SparkleIcon className="size-4" weight="regular" />
            </span>
            <span className="min-w-0">
              <span className="block text-caption font-medium text-foreground">{copy.aiLabel}</span>
              <span className="mt-0.5 block text-detail leading-snug text-muted-foreground">
                {copy.aiDescription}
              </span>
            </span>
          </DropdownMenuItem>
          <DropdownMenuItem
            className="items-start gap-2.5 rounded-lg px-2.5 py-2.5"
            disabled={isCreatingPost}
            onSelect={() => runAfterClose(onCreatePost)}
          >
            <span className="mt-0.5 flex size-5 shrink-0 items-center justify-center text-muted-foreground">
              <NotePencilIcon className="size-4" weight="regular" />
            </span>
            <span className="min-w-0">
              <span className="block text-caption font-medium text-foreground">New draft</span>
              <span className="mt-0.5 block text-detail leading-snug text-muted-foreground">
                {copy.draftDescription}
              </span>
            </span>
          </DropdownMenuItem>
        </DropdownMenuSubContent>
      </DropdownMenuPortal>
    </DropdownMenuSub>
  );
}
